/**
 * The panels the toolbar opens beside the tools.
 *
 * Position, colour, font and every setting - each the same controls that were
 * in the right-hand column, now opened where Canva opens them: on the left,
 * one at a time, beside the tool rail. The right-hand column is gone and the
 * page has its width back.
 */

import type { ReactNode } from 'react';
import type { GraphicLayer, ShapeLayer, TextLayer } from '../model/types';
import { useEditor, useSelectedLayers } from '../state/store';
import { reorderSelected } from '../state/actions';
import { shapeDef } from '../shapes/library';
import { Arrange, DASHES, Geometry, Properties, dashName } from './Properties';
import { InkEditor } from './ColourField';
import { FillEditor } from './FillField';
import { TextPicture } from './MaskPanel';
import { FontList } from './FontField';
import { LengthSlider } from './Slider';
import { changeFamily, graphicInk, graphicWidthMm, recolourGraphic, setField, setGraphicWidth } from './edits';
import type { ColourTarget } from './ContextBar';

function Shell( { title, label, onClose, children }: { title: string; label?: string; onClose: () => void; children: ReactNode } ) {
  return (
    <aside className="tb-side" aria-label={ label ?? title }>
      <div className="tb-side__head">
        <h2 className="tb-side__title">{ title }</h2>
        <button type="button" className="tb-btn tb-btn--quiet tb-btn--icon" aria-label={ `Close ${ title.toLowerCase() }` } onClick={ onClose }>
          ✕
        </button>
      </div>
      { children }
    </aside>
  );
}

/** Order, alignment, and exact numbers. */
export function PositionPanel( { onClose }: { onClose: () => void } ) {
  const selected = useSelectedLayers();
  const only = selected.length === 1 ? selected[ 0 ] : null;

  return (
    <Shell title="Position" onClose={ onClose }>
      { selected.length === 0 ? (
        <p className="tb-side__empty">Select something to move it.</p>
      ) : (
        <div className="tb-props tb-props--side">
          <section className="tb-props__section">
            <h2 className="tb-props__heading">Order</h2>
            <div className="tb-field__row">
              <button type="button" className="tb-btn" onClick={ () => reorderSelected( 'up' ) }>
                Forward
              </button>
              <button type="button" className="tb-btn" onClick={ () => reorderSelected( 'down' ) }>
                Backward
              </button>
            </div>
            <div className="tb-field__row">
              <button type="button" className="tb-btn" onClick={ () => reorderSelected( 'front' ) }>
                To front
              </button>
              <button type="button" className="tb-btn" onClick={ () => reorderSelected( 'back' ) }>
                To back
              </button>
            </div>
          </section>

          <Arrange />

          { only && (
            <section className="tb-props__section">
              <h2 className="tb-props__heading">Size and place</h2>
              <Geometry layer={ only } />
            </section>
          ) }
        </div>
      ) }
    </Shell>
  );
}

/** A colour, with the whole picker open rather than in a popover. */
export function ColourPanel( { target, onClose }: { target: ColourTarget; onClose: () => void } ) {
  const selected = useSelectedLayers();
  const layer = selected.length === 1 ? selected[ 0 ] : null;
  const background = useEditor( ( s ) => s.doc?.pages[ s.pageIndex ]?.background ?? null );

  // The page's own colour, printed edge to edge including the bleed.
  if ( target === 'page' && ! layer ) {
    return (
      <Shell title="Background" onClose={ onClose }>
        <div className="tb-colour-panel">
          <InkEditor
            value={ background?.type === 'colour' ? background.value : null }
            onChange={ ( colour ) =>
              useEditor.getState().updatePage( 'Background', ( page ) => ( { ...page, background: { type: 'colour', value: colour } } ), {
                coalesce: 'page:background',
              } )
            }
            onClear={ () => useEditor.getState().updatePage( 'Background', ( page ) => ( { ...page, background: null } ) ) }
          />
          <p className="tb-effects__hint">A background runs to the edge of the bleed, so it prints right up to the cut.</p>
        </div>
      </Shell>
    );
  }

  if ( layer?.type === 'text' ) {
    return (
      <Shell title="Text colour" onClose={ onClose }>
        <div className="tb-colour-panel">
          <FillEditor value={ layer.fill } onChange={ ( fill ) => setField( layer as TextLayer, 'fill', fill, 'Colour' ) } />
          { layer.picture && <p className="tb-effects__hint">A picture is showing inside the letters instead of this colour.</p> }
          <TextPicture layer={ layer } />
        </div>
      </Shell>
    );
  }

  if ( layer?.type === 'shape' && target === 'outline' ) return <OutlinePanel layer={ layer } onClose={ onClose } />;

  if ( layer?.type === 'shape' ) {
    return (
      <Shell title="Fill" onClose={ onClose }>
        <div className="tb-colour-panel">
          <FillEditor value={ layer.fill } onChange={ ( fill ) => setField( layer as ShapeLayer, 'fill', fill, 'Fill' ) } />
        </div>
      </Shell>
    );
  }

  if ( layer?.type === 'graphic' ) return <GraphicColour layer={ layer } onClose={ onClose } />;

  return (
    <Shell title="Colour" onClose={ onClose }>
      <p className="tb-side__empty">Select a text or a shape to colour it.</p>
    </Shell>
  );
}

/**
 * A drawing's ink, and how heavy its lines are.
 *
 * One colour for the whole drawing rather than a swatch per path: the library
 * is single-colour icons, and a panel offering eleven colours for the eleven
 * paths of a telephone would be answering a question nobody asked. A drawing
 * that arrived in several colours says so, and recolouring it is still one
 * choice - which is what "recolour" means everywhere else a customer has met
 * it.
 */
function GraphicColour( { layer, onClose }: { layer: GraphicLayer; onClose: () => void } ) {
  const ink = graphicInk( layer );
  const width = graphicWidthMm( layer );
  const mixed = ! ink && layer.paths.length > 0;

  return (
    <Shell title="Colour" onClose={ onClose }>
      <div className="tb-colour-panel">
        <InkEditor value={ ink } onChange={ ( colour ) => recolourGraphic( layer, colour ) } />

        { mixed && <p className="tb-effects__hint">This drawing is in more than one colour. Choosing one puts all of it in that colour.</p> }

        { null !== width && (
          <LengthSlider
            label="Thickness"
            mm={ width }
            minMm={ 0.05 }
            maxMm={ Math.max( 5, width * 2 ) }
            onChange={ ( v ) => setGraphicWidth( layer, v ) }
          />
        ) }
      </div>
    </Shell>
  );
}

function OutlinePanel( { layer, onClose }: { layer: ShapeLayer; onClose: () => void } ) {
  const def = shapeDef( layer.shape );
  const stroke = layer.stroke;

  return (
    <Shell title={ def.strokeOnly ? 'Line' : 'Outline' } onClose={ onClose }>
      <div className="tb-colour-panel">
        <InkEditor
          value={ stroke ? stroke.colour : null }
          onChange={ ( colour ) =>
            setField( layer, 'stroke', {
              colour,
              // A new outline needs a width, or it is set and invisible.
              width: stroke && stroke.width > 0 ? stroke.width : 0.5,
              dash: stroke ? stroke.dash : [],
            }, 'Outline' )
          }
          onClear={ def.strokeOnly ? undefined : () => setField( layer, 'stroke', null, 'Outline' ) }
        />

        { stroke && (
          <>
            <LengthSlider
              label="Width"
              mm={ stroke.width }
              minMm={ 0.1 }
              maxMm={ 5 }
              onChange={ ( v ) => setField( layer, 'stroke', { ...stroke, width: Math.max( 0.05, v ) }, 'Outline width' ) }
            />

            <div className="tb-dashes" role="radiogroup" aria-label="Line style">
              { Object.entries( DASHES ).map( ( [ name, pattern ] ) => (
                <button
                  key={ name }
                  type="button"
                  role="radio"
                  aria-checked={ dashName( stroke.dash ) === name }
                  className={ `tb-dashes__one${ dashName( stroke.dash ) === name ? ' is-on' : '' }` }
                  onClick={ () => setField( layer, 'stroke', { ...stroke, dash: pattern }, 'Line style' ) }
                >
                  <svg width="48" height="8" aria-hidden="true">
                    <line x1="2" x2="46" y1="4" y2="4" stroke="currentColor" strokeWidth="2" strokeDasharray={ pattern.map( ( v ) => v * 4 ).join( ' ' ) } />
                  </svg>
                  <span>{ name }</span>
                </button>
              ) ) }
            </div>
          </>
        ) }
      </div>
    </Shell>
  );
}

/** Every font, set in itself, searchable. */
export function FontPanel( { onClose }: { onClose: () => void } ) {
  const selected = useSelectedLayers();
  const layer = selected.length === 1 && selected[ 0 ]?.type === 'text' ? ( selected[ 0 ] as TextLayer ) : null;

  return (
    <Shell title="Font" onClose={ onClose }>
      { layer ? (
        <div className="tb-font tb-font--panel">
          <FontList value={ layer.font.family } onPick={ ( family ) => changeFamily( layer, family ) } />
        </div>
      ) : (
        <p className="tb-side__empty">Select a text to change its font.</p>
      ) }
    </Shell>
  );
}

/** Every setting there is, for the object or the page - the old properties column. */
export function SettingsPanel( { onClose }: { onClose: () => void } ) {
  return (
    <Shell title="Settings" label="Properties" onClose={ onClose }>
      <Properties />
    </Shell>
  );
}
